import React, { useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";

import Table from "rsuite/Table";
import DateRangePicker from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import "rsuite/Table/styles/index.css";
import "./style.scss";
import { usePrognosisPaymentsMutation } from "../../redux/api";

const { Column, HeaderCell, Cell } = Table;

const Prognosis = () => {
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
    const { prognosisPayments, projectId } = useAppSelector(
        (state: RootState) => state.windowSlice
    );

    const [getPrognosisPayments] = usePrognosisPaymentsMutation();

    const handleDateChange = (range: [Date, Date] | null) => {
        setDateRange(range);
    };

    const handleBlur = () => {
        if (!dateRange) {
            console.log("No date range selected");
            return;
        }

        const [startDate, endDate] = dateRange;
        const formattedStartDate = startDate.toISOString().split("T")[0];
        const formattedEndDate = endDate.toISOString().split("T")[0];

        getPrognosisPayments({
            project_id: projectId,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
        })
            .unwrap()
            .then((response) => {
                console.log("Statistics fetched successfully:", response);
            })
            .catch((error) => {
                console.error("Error fetching statistics:", error);
            });
    };

    const calculateOverallSummary = () => {
        return prognosisPayments.map((plan) => {
            const totals = plan.dates.reduce(
                (acc, { subscriptions }) => {
                    subscriptions.forEach(({ active_count, canceled_count }) => {
                        acc.activeCount += active_count;
                        acc.canceledCount += canceled_count;
                    });
                    return acc;
                },
                { activeCount: 0, canceledCount: 0 }
            );

            const totalRevenue = totals.activeCount * plan.price.amount;
            const totalCancellations = totals.canceledCount * plan.price.amount;

            return {
                planTitle: plan.plan_title,
                price: `${plan.price.amount.toFixed(2)} ${plan.price.currency}`,
                activeCount: totals.activeCount,
                totalRevenue: totalRevenue.toFixed(2),
                canceledCount: totals.canceledCount,
                totalCancellations: totalCancellations.toFixed(2),
            };
        });
    };

    const renderOverallTable = () => {
        const summaryData = calculateOverallSummary();

        return (
            <div className="plan-table">
                <h6 className="plan-title">Общий прогноз продаж</h6>
                <span className="plan-title">По всем тарифам и ценам</span>
                <Table
                    data={summaryData}
                    headerHeight={55}
                    rowHeight={30}
                    wordWrap="break-word"
                    autoHeight
                    style={{ marginTop: "15px", borderRadius: "10px", background: "white", marginBottom: "25px" }}
                >
                    <Column width={150} align="start" fixed>
                        <HeaderCell>Название тарифа</HeaderCell>
                        <Cell dataKey="planTitle" />
                    </Column>
                    <Column width={100} align="start">
                        <HeaderCell>Стоимость</HeaderCell>
                        <Cell dataKey="price" />
                    </Column>
                    <Column width={100} align="start">
                        <HeaderCell>Cписания, план</HeaderCell>
                        <Cell dataKey="activeCount" />
                    </Column>
                    <Column width={100} align="start">
                        <HeaderCell>Списания, у.е.</HeaderCell>
                        <Cell dataKey="totalRevenue" />
                    </Column>
                    <Column width={100} align="start">
                        <HeaderCell>Отмены, план</HeaderCell>
                        <Cell dataKey="canceledCount" />
                    </Column>
                    <Column width={100} align="start">
                        <HeaderCell>Отмены, у.е.</HeaderCell>
                        <Cell dataKey="totalCancellations" />
                    </Column>
                </Table>
            </div>
        )
    };

    const transformDataForTable = (plan) => {
        const tableData = plan.dates.map(({ date, subscriptions }) => {
            const activeCount = subscriptions.reduce((sum, sub) => sum + sub.active_count, 0);
            const canceledCount = subscriptions.reduce((sum, sub) => sum + sub.canceled_count, 0);
            return {
                date: new Date(date).toLocaleDateString("ru-RU"),
                activeCount,
                canceledCount,
            };
        });

        const totals = tableData.reduce(
            (acc, row) => {
                acc.activeCount += row.activeCount;
                acc.canceledCount += row.canceledCount;
                return acc;
            },
            { activeCount: 0, canceledCount: 0 }
        );

        const totalRevenue = totals.activeCount * plan.price.amount;

        tableData.push({
            date: "Итого",
            activeCount: totals.activeCount,
            canceledCount: totals.canceledCount,
        });

        tableData.push({
            date: `Итого (${plan.price.currency})`,
            activeCount: totalRevenue.toFixed(2),
            canceledCount: "",
        });

        return tableData;
    };

    const renderTable = (plan) => {
        const tableData = transformDataForTable(plan);

        return (
            <div key={plan.plan_id} className="plan-table">
                <h6 className="plan-title">{plan.plan_title}</h6>
                <span className="plan-title">{plan.price.amount} {plan.price.currency}</span>
                <Table
                    data={tableData}
                    headerHeight={55}
                    rowHeight={30}
                    wordWrap="break-word"
                    autoHeight
                    style={{ marginTop: "15px", borderRadius: "10px", background: "white", marginBottom: "25px" }}
                >
                    <Column width={150} align="start" fixed>
                        <HeaderCell>Дата</HeaderCell>
                        <Cell dataKey="date" />
                    </Column>
                    <Column width={100} align="start">
                        <HeaderCell>Списаний, шт</HeaderCell>
                        <Cell dataKey="activeCount" />
                    </Column>
                    <Column width={100} align="center">
                        <HeaderCell>Отмен, шт</HeaderCell>
                        <Cell dataKey="canceledCount" />
                    </Column>
                </Table>
            </div>
        );
    };

    

    return (
        <div className="statistic">
            <div className="statistic_container">
                <div className="statistic_wrapper">
                    <DateRangePicker
                        format="dd.MM.yyyy"
                        style={{ width: "100%", marginBottom: "15px" }}
                        size="lg"
                        placeholder="Выберите период"
                        onChange={handleDateChange}
                        onBlur={handleBlur}
                        showOneCalendar
                    />
                    {renderOverallTable()}
                    {prognosisPayments.map((plan) => renderTable(plan))}
                </div>
            </div>
        </div>
    );
};

export default Prognosis;
